import React, { useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { SeoSnapshotBackup, ScheduledReleaseItem } from '../../types/seo';

interface Props {
  open: boolean;
  onClose: () => void;
  entityId: string;
  entityType: 'PROPERTY' | 'LOCALITY' | 'HOMEPAGE';
  entityName: string;
  currentData: SeoSnapshotBackup['snapshotData'];
  backups: SeoSnapshotBackup[];
  onImmediateSync: (item: SeoSnapshotBackup) => void | Promise<void>;
  onQueueRelease: (item: ScheduledReleaseItem) => void;
  onRollback: (item: SeoSnapshotBackup) => void;
}

export default function SafeSyncQueueModal({ open, onClose, entityId, entityType, entityName, currentData, onImmediateSync }: Props) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await onImmediateSync({
        id: `draft-${Date.now()}`,
        entityId,
        entityType,
        entityName,
        timestamp: new Date().toISOString(),
        snapshotData: currentData,
        changeReason: 'Current editor contents',
        author: 'CRM user',
      });
      onClose();
    } catch {
      setError('Save failed. Your edits remain in this browser.');
    } finally {
      setSaving(false);
    }
  };

  return <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Save {entityName}</DialogTitle>
    <DialogContent dividers>
      <Stack spacing={2}>
        <Typography variant="body2">This saves the current content to the website database. Open the public URL afterward to verify what visitors see.</Typography>
        <Alert severity="info">Scheduled publishing, version restoration, and Google indexing checks are not available in this editor.</Alert>
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    </DialogContent>
    <DialogActions><Button onClick={onClose} disabled={saving}>Cancel</Button><Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save to website database'}</Button></DialogActions>
  </Dialog>;
}
